import { useState, useEffect, useRef } from 'react';
import { ConversationList } from './components/ConversationList';
import { MessageViewer } from './components/MessageViewer';
import { VirtualizedMessages } from './components/VirtualizedMessages';
import { SearchBar, DateRange } from './components/SearchBar';
import { LoginScreen } from './components/LoginScreen';
import { Timeline } from './components/Timeline';
import type { Conversation, Message, SearchResult } from '@unburden/common';
import { authFetch, getToken, setToken, clearToken } from './api';

type AuthState = 'checking' | 'authed' | 'unauthed';
type BrowseMode = 'all' | 'timeline';

export default function App() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authErrorDetail, setAuthErrorDetail] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [browseMode, setBrowseMode] = useState<BrowseMode>('all');
  const [loading, setLoading] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight - 200);
  const contentRef = useRef<HTMLDivElement>(null);

  const MESSAGES_PAGE_SIZE = 500;

  useEffect(() => {
    // The OAuth callback redirects here with #token=... (or #auth_error=...).
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const token = hash.get('token');
    const error = hash.get('auth_error');
    if (token || error) {
      if (token) setToken(token);
      if (error) {
        setAuthError(error);
        setAuthErrorDetail(hash.get('auth_detail'));
      }
      history.replaceState(null, '', window.location.pathname);
    }

    const onAuthRequired = () => setAuthState('unauthed');
    window.addEventListener('auth:required', onAuthRequired);

    (async () => {
      if (!getToken()) {
        setAuthState('unauthed');
        return;
      }
      try {
        const response = await authFetch('/auth/me');
        setAuthState(response.ok ? 'authed' : 'unauthed');
      } catch {
        setAuthState('unauthed');
      }
    })();

    return () => window.removeEventListener('auth:required', onAuthRequired);
  }, []);

  useEffect(() => {
    if (authState === 'authed') loadConversations();
  }, [authState]);

  const handleSignOut = () => {
    clearToken();
    setAuthState('unauthed');
  };

  // On mobile the main panel is `display:none` (via the `hidden` class) until
  // a conversation/search is open, so it measures 0 at mount — recompute
  // whenever it toggles visible, not just on window resize.
  const showMain = isSearchMode || Boolean(selectedConversation);

  useEffect(() => {
    const handleResize = () => {
      if (contentRef.current) {
        const height = contentRef.current.clientHeight - 60; // Subtract header
        setViewportHeight(Math.max(200, height));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMain]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/conversations');
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSelect = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    setMessagesTotal(0);
    setLoading(true);
    try {
      const response = await authFetch(
        `/api/conversations/${encodeURIComponent(conversation.id)}/messages?limit=${MESSAGES_PAGE_SIZE}&offset=0`
      );
      const data = await response.json();
      setMessages(data.messages || []);
      setMessagesTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreMessages = async () => {
    if (!selectedConversation || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await authFetch(
        `/api/conversations/${encodeURIComponent(selectedConversation.id)}/messages?limit=${MESSAGES_PAGE_SIZE}&offset=${messages.length}`
      );
      const data = await response.json();
      setMessages(prev => [...prev, ...(data.messages || [])]);
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = async (query: string, range: DateRange) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setIsSearchMode(true);
    try {
      const response = await authFetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          limit: 500,
          startDate: range.startDate,
          endDate: range.endDate,
        }),
      });
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (error) {
      console.error('Failed to search:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchMode(false);
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  if (authState === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">Checking session...</p>
      </div>
    );
  }

  if (authState === 'unauthed') {
    return <LoginScreen error={authError} detail={authErrorDetail} />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar — full width on mobile, fixed width alongside content on desktop.
          Hidden on mobile once a conversation/search is open (back button returns here). */}
      <div
        className={`${showMain ? 'hidden' : 'flex'} md:flex flex-col w-full md:w-80 bg-white border-r border-gray-200 min-h-0`}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <button
            onClick={handleSignOut}
            className="text-xs text-gray-400 hover:text-gray-600"
            title="Sign out"
          >
            Sign out
          </button>
        </div>

        <SearchBar onSearch={handleSearch} />

        <div className="px-4 py-2 border-b border-gray-200 flex gap-1">
          <button
            onClick={() => setBrowseMode('all')}
            className={`px-2.5 py-1 text-xs rounded ${
              browseMode === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setBrowseMode('timeline')}
            className={`px-2.5 py-1 text-xs rounded ${
              browseMode === 'timeline' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Timeline
          </button>
        </div>

        {browseMode === 'all' ? (
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelect={handleConversationSelect}
            loading={loading}
          />
        ) : (
          <Timeline
            selectedConversation={selectedConversation}
            onSelectConversation={handleConversationSelect}
          />
        )}
      </div>

      {/* Main Content — hidden on mobile until a conversation/search is open. */}
      <div
        ref={contentRef}
        className={`${showMain ? 'flex' : 'hidden'} md:flex flex-1 flex-col min-h-0`}
      >
        {isSearchMode ? (
          <>
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Search Results
                </h2>
                <button
                  onClick={handleClearSearch}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  Clear
                </button>
              </div>
              <p className="text-sm text-gray-600">
                {searchResults.length} results for "{searchQuery}"
              </p>
            </div>

            <div className="flex-1 overflow-hidden min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Searching...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No results found</p>
                </div>
              ) : (
                <VirtualizedMessages
                  messages={searchResults.map(r => r.message)}
                  height={viewportHeight}
                  loading={false}
                />
              )}
            </div>
          </>
        ) : selectedConversation ? (
          <>
            <div className="p-4 border-b border-gray-200 bg-white">
              <button
                onClick={handleBackToList}
                className="md:hidden mb-1 text-sm text-blue-600 hover:underline"
              >
                &lsaquo; Back
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedConversation.name || selectedConversation.participants.join(', ')}
              </h2>
              <p className="text-sm text-gray-600">
                Showing {messages.length} of {messagesTotal || selectedConversation.messageCount} messages
              </p>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <div className="flex-1 overflow-hidden min-h-0">
                {messages.length > 100 ? (
                  <VirtualizedMessages
                    messages={messages}
                    height={viewportHeight - (messages.length < messagesTotal ? 48 : 0)}
                    loading={loading}
                  />
                ) : (
                  <MessageViewer messages={messages} loading={loading} />
                )}
              </div>
              {!loading && messages.length < messagesTotal && (
                <button
                  onClick={handleLoadMoreMessages}
                  disabled={loadingMore}
                  className="py-3 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-200 disabled:opacity-50"
                >
                  {loadingMore
                    ? 'Loading more...'
                    : `Load ${Math.min(MESSAGES_PAGE_SIZE, messagesTotal - messages.length)} more messages (${messagesTotal - messages.length} remaining)`}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center flex-1">
            <p className="text-gray-500">Select a conversation or search to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
